// Port of the `GPXError` enum of TrailBivvyKit/Sources/GPX/GPXModels.swift.
//
// The Swift enum has associated values, so it becomes a `{ kind }` union (`GPXErrorReason`) carried by an `Error`
// subclass. The `message` of the error is diagnostic text for logs, never for display (src/i18n localises `reason`).

/** Why a GPX file could not be read or imported (Swift `GPXError` cases). */
export type GPXErrorReason =
  /**
   * The data is not well-formed XML. `line` is 1-based; `message` is diagnostic text from the XML parser
   * (for logs, not for display).
   */
  | { kind: 'malformedXML'; line: number; message: string }
  /** Well-formed (or empty) input whose root element is not `<gpx>`. */
  | { kind: 'notGPX' }
  /** The document has no usable line geometry (no track or route with at least two points). */
  | { kind: 'noGeometry' }
  /**
   * A `<wpt>`, `<rtept>` or `<trkpt>` has a missing, non-numeric or out-of-range `lat`/`lon` attribute.
   * `line` is the 1-based line of the offending element.
   */
  | { kind: 'invalidCoordinate'; line: number }
  /** The input exceeds `GPXParser.maximumFileSize` (UTF-8 bytes). */
  | { kind: 'fileTooLarge'; bytes: number };

/** Error thrown by `GPXParser` and `GPXConverter`; `reason` is the Swift `GPXError` case. */
export class GPXError extends Error {
  readonly reason: GPXErrorReason;

  constructor(reason: GPXErrorReason) {
    super(describeReason(reason));
    this.name = 'GPXError';
    this.reason = reason;
  }
}

function describeReason(reason: GPXErrorReason): string {
  switch (reason.kind) {
    case 'malformedXML':
      return `GPX malformedXML at line ${reason.line}: ${reason.message}`;
    case 'notGPX':
      return 'GPX notGPX: the root element is not <gpx>';
    case 'noGeometry':
      return 'GPX noGeometry: no track or route with at least two points';
    case 'invalidCoordinate':
      return `GPX invalidCoordinate at line ${reason.line}`;
    case 'fileTooLarge':
      return `GPX fileTooLarge: ${reason.bytes} bytes`;
  }
}
